import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { command, field, getObject, type Row } from './api/client';
import { statusLabel } from './i18n';
import { Select, Button, Empty, ErrorState, Field, Loading, useToast } from './ui';

type Feedback = {
  version: number;
  scorecardTemplateVersionId: string | null;
  criteria: Row[];
  canSubmit: boolean;
  feedback: {
    recommendation?: string;
    answers?: { criterionId: string; score: number; comment: string | null }[];
  } | null;
};
export function InterviewFeedbackPage({ id, onClose }: { id: string; onClose: () => void }) {
  const query = useQuery({
    queryKey: ['interview-feedback', id],
    queryFn: ({ signal }) =>
      getObject<Feedback>('/api/v1/interviews/{id}/feedback-form', { id }, signal),
  });
  if (query.isLoading) return <Loading />;
  if (query.error || !query.data) return <ErrorState error={query.error} />;
  return <FeedbackForm key={query.data.version} id={id} data={query.data} onClose={onClose} />;
}
function FeedbackForm({ id, data, onClose }: { id: string; data: Feedback; onClose: () => void }) {
  const [recommendation, setRecommendation] = useState(data.feedback?.recommendation || 'HIRE');
  const [answers, setAnswers] = useState(data.feedback?.answers || []);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const client = useQueryClient();
  const toast = useToast();
  const update = (criterionId: string, patch: { score?: number; comment?: string }) =>
    setAnswers((rows) => {
      const old = rows.find((row) => row.criterionId === criterionId) || {
        criterionId,
        score: 3,
        comment: null,
      };
      return [...rows.filter((row) => row.criterionId !== criterionId), { ...old, ...patch }];
    });
  return (
    <section className="form-page">
      <h1>Обратная связь по интервью</h1>
      {!data.canSubmit && (
        <Empty
          title="Только просмотр"
          subtitle="Заполнить форму может участник интервью с правом добавлять обратную связь."
        />
      )}
      <form
        className="domain-form"
        onSubmit={async (event) => {
          event.preventDefault();
          setPending(true);
          setError(null);
          try {
            await command(
              'POST',
              '/api/v1/interviews/{id}/feedback',
              {
                version: data.version,
                recommendation,
                scorecardTemplateVersionId: data.scorecardTemplateVersionId,
                answers: data.criteria.map(
                  (criterion) =>
                    answers.find((answer) => answer.criterionId === criterion.id) || {
                      criterionId: criterion.id,
                      score: 3,
                      comment: null,
                    },
                ),
              },
              { id },
            );
            await client.invalidateQueries();
            toast('Обратная связь сохранена');
            onClose();
          } catch (cause) {
            setError(cause as Error);
          } finally {
            setPending(false);
          }
        }}
      >
        {data.criteria.map((criterion) => {
          const answer = answers.find((item) => item.criterionId === criterion.id);
          return (
            <section className="assessment-question" key={criterion.id}>
              <h2>{field(criterion, 'title') || field(criterion, 'name')}</h2>
              <p className="muted">{field(criterion, 'description')}</p>
              <div className="rating-options">
                {[1, 2, 3, 4, 5].map((score) => (
                  <label key={score}>
                    <input
                      type="radio"
                      name={`score-${criterion.id}`}
                      value={score}
                      checked={(answer?.score ?? 3) === score}
                      disabled={!data.canSubmit}
                      onChange={() => update(criterion.id, { score })}
                    />
                    {score}
                  </label>
                ))}
              </div>
              <Field label="Комментарий">
                <textarea
                  name={`comment-${criterion.id}`}
                  value={answer?.comment || ''}
                  disabled={!data.canSubmit}
                  onChange={(event) => update(criterion.id, { comment: event.target.value })}
                />
              </Field>
            </section>
          );
        })}
        <label className="field">
          <span>Рекомендация</span>
          <Select
            name="recommendation"
            value={recommendation}
            disabled={!data.canSubmit}
            onChange={(event) => setRecommendation(event.target.value)}
          >
            {['STRONG_HIRE', 'HIRE', 'NO_HIRE', 'STRONG_NO_HIRE'].map((value) => (
              <option key={value} value={value}>
                {statusLabel(value)}
              </option>
            ))}
          </Select>
        </label>
        {error && <ErrorState error={error} />}
        <footer>
          <Button onClick={onClose}>Отмена</Button>
          {data.canSubmit && (
            <Button className="primary" type="submit" disabled={pending}>
              {pending ? 'Сохранение…' : 'Сохранить'}
            </Button>
          )}
        </footer>
      </form>
    </section>
  );
}
